import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Result } from 'antd';
import type { ResultStatusType } from 'antd/lib/result';
import debug from 'debug';
import { useRouter } from 'views/useRouter';

import { classnames } from 'lib/classnames';
import { inIframe } from 'lib/env';
import { useI18n, useToken } from 'lib/hooks';
import { useWorkspacePagePure } from 'lib/hooks/useWorkspacePagePure';
import { Item } from 'lib/models';
import { getSourcePath } from 'lib/path';
import { sendUrlToParent } from 'lib/window';

import styles from './index.module.less';

const cx = classnames(styles);
const logger = debug('itemMove:finishView');

interface FinishViewProps {
  itemId: string;
  result: { status: ResultStatusType; message?: string; data?: Parse.Attributes };
  title?: string;
  updateBackLink?: (string) => void;
  jumpStep?: (index: number) => void;
}

const FinishView: React.FC<FinishViewProps> = ({ result, itemId, title, updateBackLink, jumpStep }) => {
  const { t } = useI18n();
  const { tenant } = useToken();
  const router = useRouter();
  const [item, setItem] = useState(null);
  const [isFinish, setIsFinish] = useState(false);
  const message = useMemo(
    () => (result.status === 'success' ? t('pages.itemMove.moveSuccess') : t('pages.itemMove.moveFailed')),
    [result, t],
  );

  const pure = useWorkspacePagePure();

  const back = useCallback(() => {
    const getLink = (workspaceKey: string): string => {
      let redirectUrl = `/${tenant}/${workspaceKey}/proxima/all/boards`;
      if (router.query?.from && pure && !inIframe()) {
        redirectUrl = getSourcePath(`/${tenant}/workspaces/${workspaceKey}`);
      }
      return redirectUrl;
    };
    const openLink = getLink(item?.workspace?.key || result.data?.workspace?.key);
    logger('back', item?.workspace?.key, tenant, router.query?.from, pure, openLink);
    if (inIframe()) {
      sendUrlToParent(openLink);
    } else {
      window.open(openLink, '_self');
    }
  }, [item?.workspace?.key, result.data?.workspace?.key, router.query?.from, tenant, pure]);

  logger('init', item?.workspace?.key, tenant, router.query?.from, pure);

  const first = useCallback(() => jumpStep?.(1), [jumpStep]);

  useEffect(() => {
    const getItemDetail = async () => {
      const itemDetail = await new Parse.Query(Item).equalTo('objectId', itemId).include('workspace').first();
      if (itemDetail) {
        const itemData = itemDetail.toJSON();
        setItem(itemDetail.toJSON());
        updateBackLink && updateBackLink(itemData.workspace?.key);
      }
      setIsFinish(true);
    };
    result.status === 'success' && getItemDetail();
  }, [itemId, result.status, updateBackLink]);

  const extra = useMemo(() => {
    if (!isFinish) return;
    if (result.status === 'success' && item) {
      return [
        <Button key="back" className={cx('next-step-btn')} type="primary" onClick={back}>
          {t('pages.itemMove.back')}
        </Button>,
      ];
    }
    return [
      <Button key="backFirst" className={cx('next-step-btn')} type="primary" onClick={first}>
        {t('pages.itemMove.backToFirst')}
      </Button>,
      <Button key="back" className={cx('next-step-btn')} onClick={back}>
        {t('pages.itemMove.back')}
      </Button>,
    ];
  }, [back, first, isFinish, item, result.status, t]);

  return (
    <div className={cx('finish-wrap')}>
      {title && <div className={cx('title')}>{title}</div>}
      <Result status={result.status} title={message} subTitle={result.message} extra={extra} />
    </div>
  );
};

export default FinishView;
